/**
 * Progress — duplex v3 §4: a short spoken line for a MAIN tool call ("checking the board…") so a long tool run is not
 * dead air. A per-tool template table first (0ms, per language); else the fast model writes 2-5 words from the tool's
 * NAME and a short args hint only — never the history. A line never states a result (RESULT_CLAIM_RE), never repeats,
 * and none follows once MAIN's text has streamed this turn. The caller (VoiceCall) owns timing: speak only after ≥3s of
 * silence, at most one per `gapMs`. Lines go to the host's speak/show only — never to MAIN's history.
 */
import { forComponent } from '../logging';
import type { ChatLike, ChatResponse } from '../llm';
import { cleanLine, languageName, norm } from './channelLang';
import { RESULT_CLAIM_RE } from './leak';

const log = forComponent('Progress');
/** Hebrew result claims ("מצאתי", "סיימתי", "אין …") — RESULT_CLAIM_RE is English. */
const HE_RESULT_CLAIM = /(?:^|\s)(?:מצאתי|סיימתי|בוצע|הנה|אין)(?=\s|$|[.,!…])/u;

type Topic = 'board' | 'search' | 'web' | 'read' | 'write' | 'run' | 'mail' | 'calendar' | 'memory';
/** Tool name → topic (first match wins; names are lowercased, MCP prefixes included). */
const TOPICS: [RegExp, Topic][] = [
  [/kanban|board|card|\bkb\b|list_cards/, 'board'],
  [/mail|gmail|inbox/, 'mail'],
  [/calendar|event|schedule/, 'calendar'],
  [/memory|recall|remember/, 'memory'],
  [/web_?search|websearch|fetch|browse|url|page|tab|browser/, 'web'],
  [/grep|glob|search|find|lookup|quicklook/, 'search'],
  [/edit|write|patch|create|update|save|delete|move/, 'write'],
  [/shell|bash|exec|run|command|terminal/, 'run'],
  [/read|open|view|cat|look|get|list|ls/, 'read'],
];
/** Two phrasings per topic and language (never the same line twice in a call). */
export const PROGRESS_TEMPLATES: Record<string, Record<Topic, string[]>> = {
  en: {
    board: ['checking the board…', 'looking at the cards…'], mail: ['checking the mail…', 'going through the inbox…'],
    calendar: ['checking the calendar…', 'looking at the schedule…'], memory: ['checking my notes…', 'looking back…'],
    web: ['reading the page…', 'looking it up online…'], search: ['searching…', 'still looking…'],
    write: ['making the change…', 'writing it down…'], run: ['running it…', 'still running…'], read: ['reading it…', 'going through it…'],
  },
  he: { // plural present: gender-neutral for a voice of either gender
    board: ['בודקים בלוח…', 'מסתכלים על הכרטיסים…'], mail: ['בודקים את המייל…', 'עוברים על הדואר…'],
    calendar: ['בודקים ביומן…', 'מסתכלים בלו״ז…'], memory: ['בודקים ברשימות…', 'מחפשים בזיכרון…'],
    web: ['קוראים את הדף…', 'מחפשים ברשת…'], search: ['מחפשים…', 'עדיין מחפשים…'],
    write: ['מבצעים את השינוי…', 'כותבים את זה…'], run: ['מריצים…', 'עדיין רץ…'], read: ['קוראים…', 'עוברים על זה…'],
  },
};

export class ProgressOptions {
  templates: Record<string, Partial<Record<Topic, string[]>>> = PROGRESS_TEMPLATES;
  /** Fast-model fallback for a tool with no template (fastProgressGenerator). Absent → silence. */
  generate?: (tool: string, hint: string, lang: string, signal: AbortSignal) => Promise<string>;
  generateBudgetMs = 800;
  /** Speak only when nothing has been audible for this long. */
  silenceMs = 3000;
  /** At most one progress line per this long. */
  gapMs = 4000;
  now: () => number = () => performance.now();
}

/** A short hint of what a tool call is about: its first short string argument. */
export function argsHint(args: unknown): string {
  if (!args || typeof args !== 'object') return typeof args === 'string' ? args.slice(0, 40) : '';
  const s = Object.values(args as Record<string, unknown>).find((v) => typeof v === 'string' && v.trim());
  return typeof s === 'string' ? s.trim().replace(/\s+/g, ' ').slice(0, 40) : '';
}

export class Progress {
  private said = new Set<string>();
  private lastAt = -Infinity;
  private streamed = false;

  public constructor(public options?: Partial<ProgressOptions>) {
    this.options = { ...new ProgressOptions(), ...options };
  }
  private get o(): ProgressOptions { return this.options as ProgressOptions; }

  /** A new MAIN turn started. */
  turn(): void { this.streamed = false; }
  /** MAIN's text streamed this turn: no more progress lines for it. */
  text(): void { this.streamed = true; }
  /** May a line play now? (the caller passes when the user last heard anything) */
  due(lastAudibleAt: number): boolean {
    const now = this.o.now();
    return !this.streamed && now - lastAudibleAt >= this.o.silenceMs && now - this.lastAt >= this.o.gapMs;
  }
  /** Mark `line` spoken. */
  spoke(line: string): void { this.said.add(norm(line)); this.lastAt = this.o.now(); }

  /** The line for this tool call in `lang`, or '' (no template and no/late/invalid generation, a repeat, a result claim). */
  async line(tool: string, args: unknown, lang: string): Promise<string> {
    if (this.streamed) return '';
    const name = tool.toLowerCase();
    const topic = TOPICS.find(([re]) => re.test(name))?.[1];
    const pool = topic ? this.o.templates[lang]?.[topic] ?? [] : [];
    const fresh = pool.find((l) => !this.said.has(norm(l)));
    if (fresh) return fresh;
    if (!this.o.generate) return '';
    const ac = new AbortController();
    let t: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<null>((r) => { t = setTimeout(() => { ac.abort(); r(null); }, this.o.generateBudgetMs); });
    const raw = await Promise.race([this.o.generate(tool, argsHint(args), lang, ac.signal).catch((e) => { log.warn(`progress line failed: ${(e as Error)?.message ?? e}`); return null; }), late]).finally(() => clearTimeout(t));
    const l = cleanLine(raw ?? '', lang, 5);
    if (!l || RESULT_CLAIM_RE.test(l) || HE_RESULT_CLAIM.test(l) || this.said.has(norm(l)) || this.streamed) return '';
    return l;
  }
}

/** Fast-model progress writer: tool name + args hint only, no history. */
export function fastProgressGenerator(ai: ChatLike, model: string): NonNullable<ProgressOptions['generate']> {
  return async (tool, hint, lang, signal) => {
    const prompt = `An assistant on a live voice call is running the tool "${tool}"${hint ? ` (about: "${hint}")` : ''}. ` +
      `Write ONE spoken line of 2-5 words, in ${languageName(lang)}, saying what it is doing right now (like "checking the board…"). ` +
      'Never state a result or a finding. Output only the line.';
    const reasoning = /gpt-oss/.test(model) ? { providerOptions: { reasoning_effort: 'low' } } : {};
    const r = (await ai.chat({ model, messages: [{ role: 'user', content: prompt }], stream: false, signal, maxTokens: 150, ...reasoning })) as ChatResponse;
    return r.content ?? '';
  };
}
